import React, {
    useEffect,
    useRef,
    useState,
} from 'react'

import {
    Spin,
} from 'antd'

import fetch from 'auth/FetchInterceptor'

import {
    OfflineGameService,
} from 'offline'

import {
    env,
} from 'configs/EnvironmentConfig'

const API_BASE_URL =
    env.API_ENDPOINT_URL

// ======================================================
// URL DE IMAGEM
// ======================================================

const getImageUrl =
    (url) => {
        if (!url) {
            return null
        }

        if (
            url.startsWith(
                'http://',
            ) ||
            url.startsWith(
                'https://',
            ) ||
            url.startsWith(
                'data:',
            ) ||
            url.startsWith(
                'blob:',
            )
        ) {
            return url
        }

        return `${API_BASE_URL}${url}`
    }

// ======================================================
// TELA INICIAL
// ======================================================

const GameStartScreen = ({
    game,
    onStart,
}) => {
    const [
        loading,
        setLoading,
    ] = useState(true)

    const [
        config,
        setConfig,
    ] = useState(null)

    const skippedRef =
        useRef(false)

    // ======================================================
    // CARREGAR CONFIGURAÇÃO MEMORY
    // ======================================================

    useEffect(() => {
        let mounted =
            true

        const loadConfig =
            async () => {
                if (
                    !game?.id
                ) {
                    if (
                        mounted
                    ) {
                        setLoading(
                            false,
                        )
                    }

                    return
                }

                try {
                    setLoading(
                        true,
                    )

                    let data =
                        null

                    const forceOffline =
                        game?.offlineMode ===
                        true ||
                        (
                            typeof navigator !==
                            'undefined' &&
                            navigator.onLine ===
                            false
                        )

                    // ==========================================
                    // OFFLINE: INDEXEDDB PRIMEIRO
                    // ==========================================

                    if (
                        forceOffline
                    ) {
                        try {
                            data =
                                await OfflineGameService
                                    .getMemoryConfig(
                                        game.id,
                                    )

                            if (
                                data
                            ) {
                                console.log(
                                    '[MEMORY START] Configuração carregada offline.',
                                )
                            }
                        } catch (
                        offlineError
                        ) {
                            console.warn(
                                '[MEMORY START] Falha ao carregar configuração offline:',
                                offlineError,
                            )
                        }
                    }

                    // ==========================================
                    // ONLINE: API
                    // ==========================================

                    if (
                        !data &&
                        !forceOffline
                    ) {
                        try {
                            data =
                                await fetch({
                                    url:
                                        `/memory-games/${game.id}`,

                                    method:
                                        'get',

                                    silentNetworkError:
                                        true,
                                })

                            if (
                                data
                            ) {
                                try {
                                    await OfflineGameService
                                        .saveMemoryConfig(
                                            game.id,
                                            data,
                                        )
                                } catch (
                                cacheError
                                ) {
                                    console.warn(
                                        '[MEMORY START] Não foi possível atualizar cache offline:',
                                        cacheError,
                                    )
                                }
                            }
                        } catch (
                        apiError
                        ) {
                            console.warn(
                                '[MEMORY START] API indisponível. Tentando IndexedDB...',
                                apiError,
                            )
                        }
                    }

                    // ==========================================
                    // FALLBACK OFFLINE
                    // ==========================================

                    if (
                        !data
                    ) {
                        try {
                            data =
                                await OfflineGameService
                                    .getMemoryConfig(
                                        game.id,
                                    )
                        } catch (
                        offlineError
                        ) {
                            console.warn(
                                '[MEMORY START] Falha no fallback offline:',
                                offlineError,
                            )
                        }
                    }

                    if (
                        !mounted
                    ) {
                        return
                    }

                    setConfig(
                        data?.config ||
                        null,
                    )
                } catch (
                error
                ) {
                    console.error(
                        '[MEMORY START] Erro ao carregar configuração:',
                        error,
                    )

                    if (
                        mounted
                    ) {
                        setConfig(
                            null,
                        )
                    }
                } finally {
                    if (
                        mounted
                    ) {
                        setLoading(
                            false,
                        )
                    }
                }
            }

        loadConfig()

        return () => {
            mounted =
                false
        }
    }, [
        game?.id,
        game?.offlineMode,
    ])

    // ======================================================
    // TELA INICIAL DESATIVADA
    // ======================================================

    useEffect(() => {
        if (
            loading ||
            !config ||
            config
                ?.startScreenEnabled !==
            false ||
            skippedRef.current
        ) {
            return
        }

        skippedRef.current =
            true

        if (
            typeof onStart ===
            'function'
        ) {
            onStart()
        }
    }, [
        loading,
        config,
        onStart,
    ])

    // ======================================================
    // LOADING
    // ======================================================

    if (
        loading
    ) {
        return (
            <div
                style={{
                    width:
                        '100vw',

                    height:
                        '100vh',

                    display:
                        'flex',

                    alignItems:
                        'center',

                    justifyContent:
                        'center',

                    backgroundColor:
                        game?.backgroundColor ||
                        '#ffffff',
                }}
            >
                <Spin
                    size="large"
                />
            </div>
        )
    }

    /*
     * Enquanto o useEffect acima envia o usuário
     * para a próxima etapa, não mostramos a tela.
     */
    if (
        config
            ?.startScreenEnabled ===
        false
    ) {
        return null
    }

    // ======================================================
    // CONFIGURAÇÕES
    // ======================================================

    const backgroundColor =
        config
            ?.startScreenBackgroundColor ||
        game
            ?.backgroundColor ||
        '#ffffff'

    const backgroundUrl =
        config
            ?.startScreenBackgroundUrl ||
        null

    const logoUrl =
        config
            ?.startScreenLogoUrl ||
        game
            ?.logoUrl ||
        null

    const showLogo =
        config
            ?.startScreenShowLogo !==
        false

    const logoWidth =
        Math.max(
            40,
            Number(
                config
                    ?.startScreenLogoWidth ||
                220,
            ),
        )

    const logoHeight =
        Math.max(
            40,
            Number(
                config
                    ?.startScreenLogoHeight ||
                220,
            ),
        )

    const title =
        config
            ?.startScreenTitle ||
        'DESAFIO DA MEMÓRIA'

    const showTitle =
        config
            ?.startScreenShowTitle !==
        false

    const titleColor =
        config
            ?.startScreenTitleColor ||
        game
            ?.textColor ||
        '#0d3f9f'

    const titleFontSize =
        Math.max(
            12,
            Number(
                config
                    ?.startScreenTitleFontSize ||
                76,
            ),
        )

    const subtitle =
        config
            ?.startScreenSubtitle ||
        'Memorize as cartas e encontre todos os pares no menor tempo possível!'

    const showSubtitle =
        config
            ?.startScreenShowSubtitle !==
        false

    const subtitleColor =
        config
            ?.startScreenSubtitleColor ||
        titleColor

    const subtitleFontSize =
        Math.max(
            12,
            Number(
                config
                    ?.startScreenSubtitleFontSize ||
                28,
            ),
        )

    const buttonText =
        config
            ?.startScreenButtonText ||
        'JOGAR'

    const buttonColor =
        config
            ?.startScreenPrimaryColor ||
        game
            ?.primaryColor ||
        '#1677ff'

    const buttonTextColor =
        config
            ?.startScreenButtonTextColor ||
        buttonColor

    const buttonSize =
        Math.max(
            60,
            Number(
                config
                    ?.startScreenPlayButtonSize ||
                150,
            ),
        )

    const hintText =
        config
            ?.startScreenHintText ||
        'Toque para iniciar'

    const showHint =
        config
            ?.startScreenShowHint !==
        false

    const hintColor =
        config
            ?.startScreenHintColor ||
        titleColor

    const hintFontSize =
        Math.max(
            10,
            Number(
                config
                    ?.startScreenHintFontSize ||
                21,
            ),
        )

    // ======================================================
    // JOGAR
    // ======================================================

    const handleStart =
        () => {
            if (
                typeof onStart ===
                'function'
            ) {
                onStart()
            }
        }

    // ======================================================
    // TELA
    // ======================================================

    return (
        <div
            style={{
                position:
                    'relative',

                width:
                    '100vw',

                height:
                    '100vh',

                minHeight:
                    '100vh',

                overflow:
                    'hidden',

                display:
                    'flex',

                alignItems:
                    'center',

                justifyContent:
                    'center',

                boxSizing:
                    'border-box',

                backgroundColor,

                backgroundImage:
                    backgroundUrl
                        ? `url(${getImageUrl(
                            backgroundUrl,
                        )})`
                        : undefined,

                backgroundSize:
                    'cover',

                backgroundPosition:
                    'center',

                backgroundRepeat:
                    'no-repeat',
            }}
        >
            {/* ================================================== */}
            {/* CONTEÚDO */}
            {/* ================================================== */}

            <div
                style={{
                    position:
                        'relative',

                    zIndex:
                        2,

                    width:
                        '100%',

                    height:
                        '100%',

                    display:
                        'flex',

                    flexDirection:
                        'column',

                    alignItems:
                        'center',

                    justifyContent:
                        'center',

                    textAlign:
                        'center',

                    padding:
                        'clamp(24px, 4vw, 80px)',

                    boxSizing:
                        'border-box',
                }}
            >
                {/* ============================================== */}
                {/* LOGO */}
                {/* ============================================== */}

                {showLogo &&
                    logoUrl && (
                        <img
                            src={
                                getImageUrl(
                                    logoUrl,
                                )
                            }
                            alt="Logo"
                            draggable={
                                false
                            }
                            style={{
                                width:
                                    logoWidth,

                                height:
                                    logoHeight,

                                maxWidth:
                                    '75vw',

                                maxHeight:
                                    '28vh',

                                objectFit:
                                    'contain',

                                marginBottom:
                                    30,

                                userSelect:
                                    'none',
                            }}
                        />
                    )}

                {/* ============================================== */}
                {/* TÍTULO */}
                {/* ============================================== */}

                {showTitle &&
                    title && (
                        <div
                            style={{
                                maxWidth:
                                    1400,

                                color:
                                    titleColor,

                                fontSize:
                                    titleFontSize,

                                fontWeight:
                                    900,

                                lineHeight:
                                    1.05,

                                marginBottom:
                                    18,
                            }}
                        >
                            {
                                title
                            }
                        </div>
                    )}

                {/* ============================================== */}
                {/* SUBTÍTULO */}
                {/* ============================================== */}

                {showSubtitle &&
                    subtitle && (
                        <div
                            style={{
                                maxWidth:
                                    1000,

                                color:
                                    subtitleColor,

                                fontSize:
                                    subtitleFontSize,

                                fontWeight:
                                    500,

                                lineHeight:
                                    1.4,

                                marginBottom:
                                    42,
                            }}
                        >
                            {
                                subtitle
                            }
                        </div>
                    )}

                {/* ============================================== */}
                {/* PLAY */}
                {/* ============================================== */}

                <button
                    type="button"
                    onClick={
                        handleStart
                    }
                    style={{
                        position:
                            'relative',

                        width:
                            buttonSize,

                        height:
                            buttonSize,

                        minWidth:
                            buttonSize,

                        minHeight:
                            buttonSize,

                        padding:
                            0,

                        borderRadius:
                            '50%',

                        border:
                            `5px solid ${buttonColor}`,

                        background:
                            buttonColor,

                        color:
                            '#ffffff',

                        cursor:
                            'pointer',

                        display:
                            'flex',

                        alignItems:
                            'center',

                        justifyContent:
                            'center',

                        boxShadow:
                            '0 16px 38px rgba(0,0,0,.18)',

                        transition:
                            'transform .2s ease',
                    }}
                >
                    {/* TRIÂNGULO PLAY */}

                    <div
                        style={{
                            width:
                                0,

                            height:
                                0,

                            marginLeft:
                                buttonSize *
                                0.08,

                            borderTop:
                                `${buttonSize * 0.18}px solid transparent`,

                            borderBottom:
                                `${buttonSize * 0.18}px solid transparent`,

                            borderLeft:
                                `${buttonSize * 0.28}px solid #ffffff`,
                        }}
                    />
                </button>

                {/* ============================================== */}
                {/* JOGAR */}
                {/* ============================================== */}

                {buttonText && (
                    <div
                        style={{
                            marginTop:
                                16,

                            color:
                                buttonTextColor,

                            fontSize:
                                Math.max(
                                    20,
                                    buttonSize *
                                    0.25,
                                ),

                            fontWeight:
                                900,

                            lineHeight:
                                1,
                        }}
                    >
                        {
                            buttonText
                        }
                    </div>
                )}

                {/* ============================================== */}
                {/* TEXTO AUXILIAR */}
                {/* ============================================== */}

                {showHint &&
                    hintText && (
                        <div
                            style={{
                                marginTop:
                                    8,

                                color:
                                    hintColor,

                                fontSize:
                                    hintFontSize,

                                fontWeight:
                                    500,

                                opacity:
                                    0.85,
                            }}
                        >
                            {
                                hintText
                            }
                        </div>
                    )}
            </div>
        </div>
    )
}

export default GameStartScreen